import FilterDate from '@/Enums/FilterDate.enum';
import DateFormat from '@/Enums/Format/DateFormat.enum';
import { IFootTrafficCampaignControlOption } from '@/Interfaces/Attribution/State.interface';
import { IClientControlOption, IControlOption, IWhitelabelControlOption } from '@/Interfaces/ControlOption.interface';
import ClearAllButton from '@/components/Buttons/ClearAllButton';
import SupportRequestModal from '@/components/Modal/SupportRequestModal';
import { mapWhitelabelToControlOption } from '@/helpers/Whitelabels';
import { areArraysOfObjectsEqual } from '@/helpers/comparators';
import { getFilterOptions } from '@/helpers/filters/filterOptions';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  selectFootTrafficCampaignFilterOptions,
  selectFootTrafficClients,
  selectFootTrafficFilters,
  selectFootTrafficWhitelabels,
} from '@/store/selectors/attribution/footTraffic.selectors';
import { selectMeRole } from '@/store/selectors/me.selectors';
import { selectSharedApiLoading } from '@/store/selectors/sharedApi.selectors';
import {
  getFootTrafficCampaignOptions,
  getFootTrafficLocationOptions,
  resetFilters,
  setFilters,
} from '@/store/slices/attribution/footTraffic.slice';
import { FootTrafficFilters } from '@/types/Attribution/filters.types';
import { PrintOutlined } from '@mui/icons-material';
import { Box, Button, Paper } from '@mui/material';
import { DateRange } from '@mui/x-date-pickers-pro';
import { Moment } from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import VenueCoverageModal from '../../VenueCoverageModal';
import FilterPopover from './FilterPopover';
import FilterSelects from './FilterSelects';
import RankingBadge from './RankingBadge';

interface IProps {
  hideWhitelabels: boolean;
  hideClients: boolean;
}

const campaignsReset: Partial<FootTrafficFilters> = { lineItems: [] };
const advertisersReset: Partial<FootTrafficFilters> = { ...campaignsReset, campaigns: [], locations: [] };
const clientsReset: Partial<FootTrafficFilters> = { ...advertisersReset, advertisers: [] };
const whitelabelsReset: Partial<FootTrafficFilters> = { ...clientsReset, clients: [] };

const Filters = ({ hideWhitelabels, hideClients }: IProps) => {
  const dispatch = useAppDispatch();
  //#region state, selectors, memo
  const [requestAddressesModalOpen, setRequestAddressesModalOpen] = useState<boolean>(false);
  const [venueCoverageModalOpen, setVenueCoverageModalOpen] = useState<boolean>(false);

  const meRole = useAppSelector(selectMeRole);
  const clients = useAppSelector(selectFootTrafficClients);
  const whitelabels = useAppSelector(selectFootTrafficWhitelabels);
  const loadingFilterSource = useAppSelector(selectSharedApiLoading);
  const { campaigns, loading: loadingCampaigns } = useAppSelector(selectFootTrafficCampaignFilterOptions);

  const {
    dateRangeStart,
    dateRangeEnd,
    dateRangeFormat,
    whitelabels: filterWhitelabels,
    clients: filterClients,
    advertisers: filterAdvertisers,
    campaigns: filterCampaigns,
  } = useAppSelector(selectFootTrafficFilters);

  const clientsDisabled = useMemo(
    () => (hideWhitelabels ? loadingFilterSource : loadingFilterSource || !filterWhitelabels?.length),
    [hideWhitelabels, loadingFilterSource, filterWhitelabels?.length],
  );

  const options = useMemo(() => {
    return getFilterOptions({
      userRole: meRole,
      whitelabels,
      selectedWhitelabels: filterWhitelabels,
      clients,
      selectedClients: filterClients,
    });
  }, [meRole, whitelabels, clients, filterClients, filterWhitelabels]);
  //#endregion

  //#region callback
  const _setFilters = useCallback((filters: Partial<FootTrafficFilters>) => dispatch(setFilters(filters)), [dispatch]);
  const loadCampaigns = useCallback(() => dispatch(getFootTrafficCampaignOptions({})), [dispatch]);

  const setDateRange = useCallback(
    (range: DateRange<Moment>, format?: FilterDate) => {
      const newStartDate = range[0]?.format(DateFormat.Iso8601);
      const newEndDate = range[1]?.format(DateFormat.Iso8601);
      const isValidChangeEvent = newStartDate && newEndDate;

      if (!isValidChangeEvent) return;

      if (dateRangeStart !== newStartDate || dateRangeEnd !== newEndDate || dateRangeFormat !== format) {
        _setFilters({ dateRangeStart: newStartDate, dateRangeEnd: newEndDate, dateRangeFormat: format });
      }
    },
    [dateRangeStart, dateRangeEnd, dateRangeFormat, _setFilters],
  );

  const onWhitelabelsChange = useCallback(
    (updatedWhitelabels: IWhitelabelControlOption[] = []) => {
      if (!areArraysOfObjectsEqual(updatedWhitelabels, filterWhitelabels)) {
        _setFilters({ whitelabels: updatedWhitelabels, ...whitelabelsReset });
      }
    },
    [_setFilters, filterWhitelabels],
  );

  const onClientsChange = useCallback(
    (updatedClients: IClientControlOption[] = []) => {
      if (!areArraysOfObjectsEqual(updatedClients, filterClients)) {
        _setFilters({ clients: updatedClients, ...clientsReset });
      }
    },
    [_setFilters, filterClients],
  );

  const onAdvertiserChange = useCallback(
    (updatedAdvertisers: IControlOption[] = []) => {
      if (areArraysOfObjectsEqual(updatedAdvertisers, filterAdvertisers)) {
        return;
      }
      _setFilters({ advertisers: updatedAdvertisers, ...advertisersReset });
      if (updatedAdvertisers?.length) {
        loadCampaigns();
      }
    },
    [_setFilters, filterAdvertisers, loadCampaigns],
  );

  const onCampaignsChange = useCallback(
    (updatedCampaigns: IFootTrafficCampaignControlOption[] = []) => {
      if (!areArraysOfObjectsEqual(updatedCampaigns, filterCampaigns)) {
        _setFilters({ campaigns: updatedCampaigns, ...campaignsReset });
      }
    },
    [_setFilters, filterCampaigns],
  );

  const handlerClear = useCallback(() => dispatch(resetFilters()), [dispatch]);

  const onRequestAddressesClick = useCallback(() => {
    setRequestAddressesModalOpen(true);
  }, []);

  const onRequestAddressesClose = useCallback(() => {
    setRequestAddressesModalOpen(false);
  }, []);

  const onPrintClick = useCallback(() => {
    window.print();
  }, []);

  const onVenueCoverageClick = useCallback(() => {
    setVenueCoverageModalOpen(true);
  }, []);

  const onVenueCoverageClose = useCallback(() => {
    setVenueCoverageModalOpen(false);
  }, []);

  //#endregion

  useEffect(() => {
    if (whitelabels?.length === 1) {
      _setFilters({ whitelabels: whitelabels.map(mapWhitelabelToControlOption), ...whitelabelsReset });
    }
  }, [loadingFilterSource, _setFilters]);

  // Pre-fetch location options when advertiser selection changes so the popover opens with options ready.
  useEffect(() => {
    if (filterAdvertisers?.length) {
      dispatch(getFootTrafficLocationOptions({}));
    }
  }, [filterAdvertisers, dispatch]);

  return (
    <Paper className='h-max p-[24px] pt-[20px] flex-wrap' sx={{ '@media print': { display: 'none' } }}>
      <div className='flex justify-between mb-[16px]'>
        <div className='flex items-center gap-2'>
          <h2 className='text-[#020202] font-bold text-2xl'>Filtering</h2>
          <RankingBadge />
        </div>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            variant='outlined'
            size='small'
            startIcon={<PrintOutlined />}
            onClick={onPrintClick}
            sx={{ fontSize: 14 }}
          >
            Print
          </Button>
          <Button
            variant='outlined'
            size='small'
            onClick={onVenueCoverageClick}
            disabled={!filterAdvertisers?.length}
            sx={{ fontSize: 14 }}
          >
            Coverage
          </Button>
          <ClearAllButton onClick={handlerClear} sx={{ fontSize: 14 }} />
        </Box>
      </div>
      <FilterSelects
        dateStart={dateRangeStart}
        dateEnd={dateRangeEnd}
        dateRangeFormat={dateRangeFormat}
        setDateRange={setDateRange}
        whitelabels={options.whitelabel as IWhitelabelControlOption[]}
        filterWhitelabels={filterWhitelabels}
        whitelabelsDisabled={loadingFilterSource}
        hideWhitelabels={hideWhitelabels}
        onWhitelabelsChange={onWhitelabelsChange}
        clients={options.client}
        filterClients={filterClients}
        clientsDisabled={clientsDisabled}
        hideClients={hideClients}
        onClientsChange={onClientsChange}
        advertisers={options.advertiser as IControlOption[]}
        filterAdvertisers={filterAdvertisers}
        onAdvertisersChange={onAdvertiserChange}
        campaigns={campaigns}
        filterCampaigns={filterCampaigns}
        onCampaignsChange={onCampaignsChange}
        loadingCampaigns={loadingCampaigns}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '15px' }}>
        <FilterPopover />
        <Button onClick={onRequestAddressesClick} variant='contained'>
          Request to Add Addresses
        </Button>
      </Box>
      <SupportRequestModal
        title='Addresses'
        isOpen={requestAddressesModalOpen}
        handleClose={onRequestAddressesClose}
        includeAdvertiser
      />
      <VenueCoverageModal isOpen={venueCoverageModalOpen} onClose={onVenueCoverageClose} />
    </Paper>
  );
};

export default Filters;
